import { Alert, Box, Skeleton, Tooltip, Typography } from '@mui/material';

import {
  useGetAgingPayables,
  useGetAgingReceivables,
  useGetCurrentQuarterStats,
  useGetFinancialYearlyTrends,
  useGetIncomingOutgoingCashflowData,
} from 'src/api/admin';

import { fCurrency } from 'src/utils/format-number';
import { useLocales } from 'src/locales';

import { sumBuckets } from '../aging-totals';
import DashboardPill from '../dashboard-pill';
import CashCycleTrendChart from './cash-cycle-trend-chart';

/**
 * NAT-2519 — the cash cycle and the near-term cash position, on the Operativa page.
 *
 * Three readings, each loading and failing on its own so one slow endpoint does not blank the
 * other two: the four cycle figures, their trend over the last twelve months, and the cash due
 * in and out in the near term.
 */

const NOT_AVAILABLE = '—';

/** The trend endpoint's rolling window. Without it, it returns twelve copies of today. */
const TREND_RANGE = 'last_12_months';

type Card = {
  key: string;
  label: string;
  value: string;
  hint: string;
  /** Shows a calm "provisional" pill next to the label, with `provisionalNote` as its tooltip. */
  provisional?: boolean;
  provisionalNote?: string;
  /** An explanation, shown as a tooltip on the figure. */
  note?: string;
};

function SubHeading({ children }: { children: string }) {
  return (
    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function CardGrid({ cards }: { cards: Card[] }) {
  const { t } = useLocales('cashCycleDashboard');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: `repeat(${cards.length}, 1fr)`,
        },
        gap: 1.5,
      }}
    >
      {cards.map((card) => (
        <Box key={card.key} sx={{ backgroundColor: 'background.neutral', borderRadius: 1, p: 3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {card.label}
          </Typography>
          {card.provisional && (
            <DashboardPill label={t('provvisorio_label')} note={card.provisionalNote} />
          )}
          <Tooltip title={card.note ?? ''} arrow describeChild>
            <Typography
              variant="h4"
              component="p"
              tabIndex={card.note ? 0 : undefined}
              sx={{ fontWeight: 'fontWeightMedium' }}
              // A bare em-dash is not announced at default punctuation levels, and a paragraph
              // cannot carry a name: the dash takes the img role so the label is valid.
              {...(card.value === NOT_AVAILABLE && {
                role: 'img',
                'aria-label': t('non_disponibile'),
              })}
            >
              {card.value}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {card.hint}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CardsSkeleton({ count }: { count: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${count}, 1fr)` },
        gap: 1.5,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} variant="rounded" height={112} />
      ))}
    </Box>
  );
}

export default function CashCycleSection() {
  const { t, currentLang } = useLocales('cashCycleDashboard');
  const lang = currentLang.value;
  const numberFormat = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'it-IT', {
    maximumFractionDigits: 0,
  });

  const cycle = useGetCurrentQuarterStats();
  const trends = useGetFinancialYearlyTrends({ year: TREND_RANGE });
  const cashflow = useGetIncomingOutgoingCashflowData();
  // The same two reports the "Da incassare" and "Da pagare" sections read. The cashflow endpoint
  // now sums from those same rows too, so its totals agree — the net position is taken directly
  // from the reports anyway, per NAT-2519, so a change to either source can't silently diverge it
  // from the sections shown above it on the same page.
  const receivables = useGetAgingReceivables();
  const payables = useGetAgingPayables();

  const days = (value: number | null | undefined) =>
    value === null || value === undefined || Number.isNaN(value)
      ? NOT_AVAILABLE
      : numberFormat.format(value);
  const money = (value: number | null | undefined) =>
    value === null || value === undefined ? NOT_AVAILABLE : fCurrency(value.toFixed(2), lang);

  // NAT-2800, rule 55 — how long customers actually took, beside DSO (the balance-based figure).
  // No payment in the window (null), or a backend that predates it (absent): no tile, not a zero.
  const paymentTime = cycle.data?.paymentTime;
  const paymentTimeCards: Card[] = paymentTime
    ? [
        {
          key: 'paymentTime',
          label: t('tempo_pagamento_label'),
          value: t('giorni_valore', { days: numberFormat.format(paymentTime.days) }),
          // The amount customers paid (rule 58: zero is shown as a zero); absent or null, the hint
          // reads as it did before.
          hint:
            paymentTime.amount === null || paymentTime.amount === undefined
              ? t('tempo_pagamento_hint', {
                  count: paymentTime.invoices,
                  invoices: numberFormat.format(paymentTime.invoices),
                })
              : t('tempo_pagamento_hint_importo', {
                  count: paymentTime.invoices,
                  invoices: numberFormat.format(paymentTime.invoices),
                  amount: fCurrency(paymentTime.amount.toFixed(2), lang),
                }),
          note: t('tempo_pagamento_nota'),
        },
      ]
    : [];

  const cycleCards: Card[] = [
    {
      key: 'dso',
      label: t('dso_label'),
      value: days(cycle.data?.dso),
      hint: t('dso_hint'),
    },
    ...paymentTimeCards,
    {
      key: 'dio',
      label: t('dio_label'),
      value: days(cycle.data?.weightedDIO),
      hint: t('dio_hint'),
    },
    {
      key: 'dpo',
      label: t('dpo_label'),
      value: days(cycle.data?.dpo),
      hint: t('dpo_hint'),
      provisional: true,
      provisionalNote: t('provvisorio_nota'),
    },
    {
      key: 'ccc',
      label: t('ccc_label'),
      value: days(cycle.data?.ccc),
      hint: t('ccc_hint'),
      provisional: true,
      provisionalNote: t('provvisorio_nota'),
    },
  ];

  const openReceivables = sumBuckets(receivables.data?.buckets);
  const openPayables = sumBuckets(payables.data?.buckets);
  const netPosition =
    openReceivables === null || openPayables === null ? null : openReceivables - openPayables;

  const nearTermCards: Card[] = [
    {
      key: 'incoming',
      label: t('entrate_7_giorni'),
      value: money(cashflow.data?.customers?.dueNext7Days),
      hint: t('entrate_7_giorni_hint'),
    },
    {
      key: 'outgoing',
      label: t('uscite_30_giorni'),
      value: money(cashflow.data?.suppliers?.dueNext30Days),
      hint: t('uscite_30_giorni_hint'),
    },
    {
      key: 'net',
      label: t('posizione_netta'),
      value: money(netPosition),
      hint: t('posizione_netta_hint'),
    },
  ];

  const nearTermLoading =
    (cashflow.isLoading && !cashflow.data) ||
    (receivables.isLoading && !receivables.data) ||
    (payables.isLoading && !payables.data);
  const nearTermFailed =
    (cashflow.error && !cashflow.data) ||
    (receivables.error && !receivables.data) ||
    (payables.error && !payables.data);
  // A revalidation failure with cached figures still on screen is not the same as a first-load
  // failure: the numbers shown are real, just possibly out of date, and hiding that behind a
  // silently-swallowed error would let a stale net position sit on screen looking current.
  const nearTermStale =
    (Boolean(cashflow.error) && Boolean(cashflow.data)) ||
    (Boolean(receivables.error) && Boolean(receivables.data)) ||
    (Boolean(payables.error) && Boolean(payables.data));

  const renderCycle = () => {
    if (cycle.isLoading && !cycle.data) return <CardsSkeleton count={cycleCards.length} />;
    if (cycle.error && !cycle.data) {
      return <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>;
    }
    return <CardGrid cards={cycleCards} />;
  };

  const renderTrend = () => {
    if (trends.isLoading && !trends.data) return <Skeleton variant="rounded" height={220} />;
    if (trends.error && !trends.data) {
      return <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>;
    }
    if (!trends.data?.length) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('trend_vuoto')}
        </Typography>
      );
    }
    return <CashCycleTrendChart months={trends.data} />;
  };

  const renderNearTerm = () => {
    if (nearTermLoading) return <CardsSkeleton count={nearTermCards.length} />;
    if (nearTermFailed) return <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Non-blocking: the cached figures below are still shown, this only flags that the
            latest refresh did not land. */}
        {nearTermStale && <Alert severity="warning">{t('aggiornamento_non_riuscito')}</Alert>}
        <CardGrid cards={nearTermCards} />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4">{t('titolo')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('sottotitolo')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SubHeading>{t('ciclo_titolo')}</SubHeading>
        {renderCycle()}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SubHeading>{t('trend_titolo')}</SubHeading>
        {renderTrend()}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SubHeading>{t('breve_titolo')}</SubHeading>
        {renderNearTerm()}
      </Box>
    </Box>
  );
}
